import React, { useState, useEffect, useRef, useCallback } from 'react'
import { apiClient, SecretKeyStatus } from '../lib'
import { useAuth } from '../contexts/AuthContext'
import CircularLoader from './CircularLoader'
import { readImpersonationId } from '../utils/impersonation'

interface SecretKeyManagerProps {
  onKeyGenerated?: (key: string) => void
}

const SecretKeyManager: React.FC<SecretKeyManagerProps> = ({ onKeyGenerated }) => {
  const {
    user,
    impersonatedUserId,
    impersonatedUserEmail,
    effectiveUserId,
  } = useAuth()
  const [status, setStatus] = useState<SecretKeyStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [generatedKey, setGeneratedKey] = useState<string | null>(null)
  const [showKey, setShowKey] = useState(false)
  const statusRequestRef = useRef(0)
  const effectiveUserIdentifier = effectiveUserId || null

  const checkStatus = useCallback(async () => {
    const requestId = ++statusRequestRef.current
    const impersonationSnapshot = readImpersonationId()
    try {
      setLoading(true)
      setError(null)
      console.log('Checking secret key status...')
      const statusData = await apiClient.checkSecretKeyStatus()
      console.log('Status response:', statusData)
      if (requestId !== statusRequestRef.current || impersonationSnapshot !== readImpersonationId()) {
        console.debug('Ignoring stale secret key status response due to impersonation change')
        return
      }
      setStatus(statusData)
    } catch (err) {
      if (requestId === statusRequestRef.current && impersonationSnapshot === readImpersonationId()) {
        console.error('Status check error:', err)
        setError(err instanceof Error ? err.message : 'Failed to check status')
      }
    } finally {
      if (requestId === statusRequestRef.current && impersonationSnapshot === readImpersonationId()) {
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    checkStatus()
  }, [checkStatus, impersonatedUserId])

  const generateKey = async () => {
    try {
      setLoading(true)
      setError(null)
      console.log('Generating secret key...')
      const response = await apiClient.generateSecretKey()
      console.log('Key generated successfully:', response.secret_key.substring(0, 8) + '...')
      setGeneratedKey(response.secret_key)
      setShowKey(true)
      setStatus({ has_secret_key: true, created_at: new Date().toISOString() })
      onKeyGenerated?.(response.secret_key)
      
      // Verify immediately after generation
      setTimeout(async () => {
        console.log('Verifying key was saved...')
        await checkStatus()
      }, 1000)
    } catch (err) {
      console.error('Key generation error:', err)
      setError(err instanceof Error ? err.message : 'Failed to generate key')
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = async () => {
    if (generatedKey) {
      try {
        await navigator.clipboard.writeText(generatedKey)
        // Show brief success feedback
        const button = document.getElementById('copy-button')
        if (button) {
          const originalText = button.textContent
          button.textContent = 'Copied!'
          setTimeout(() => {
            button.textContent = originalText
          }, 2000)
        }
      } catch (err) {
        console.error('Failed to copy to clipboard:', err)
      }
    }
  }

  const copyUserIdToClipboard = async () => {
    if (effectiveUserIdentifier) {
      try {
        await navigator.clipboard.writeText(effectiveUserIdentifier)
        // Show brief success feedback
        const button = document.getElementById('copy-user-id-button')
        if (button) {
          const originalText = button.textContent
          button.textContent = 'Copied!'
          setTimeout(() => {
            button.textContent = originalText
          }, 2000)
        }
      } catch (err) {
        console.error('Failed to copy user ID to clipboard:', err)
      }
    }
  }

  const revokeKey = async () => {
    if (window.confirm('Are you sure you want to revoke your secret key? This action cannot be undone.')) {
      try {
        setLoading(true)
        setError(null)
        await apiClient.revokeSecretKey()
        setStatus({ has_secret_key: false })
        setGeneratedKey(null)
        setShowKey(false)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to revoke key')
      } finally {
        setLoading(false)
      }
    }
  }

  if (loading && !status) {
    return (
      <div className="px-5 py-4 text-center">
        <CircularLoader size="small" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 mx-5 my-4 bg-red-950/50 border border-red-800/50 rounded-lg">
        <div className="text-red-400 text-sm mb-2">
          Error: {error}
        </div>
        <button
          onClick={checkStatus}
          className="px-3 py-1.5 text-xs bg-zinc-600 text-gray-200 border-none rounded cursor-pointer hover:bg-zinc-500 transition-colors duration-200"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="px-5 py-4 border-b border-zinc-600">
      <div className="mb-3">
        <h4 className="m-0 text-sm font-semibold text-zinc-400 uppercase tracking-wider">
          API Access
        </h4>
      </div>

      {/* User ID Display */}
      {effectiveUserIdentifier && (
        <div className="mb-3 p-3 bg-emerald-950/50 border border-emerald-800/50 rounded-md">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-emerald-400 text-xs">👤</span>
            <span className="text-gray-200 text-sm font-medium">
              {impersonatedUserId ? 'Impersonated User ID' : 'User ID'}
            </span>
          </div>
          <div className="flex gap-2 items-center">
            <input
              type="text"
              value={effectiveUserIdentifier}
              readOnly
              className="flex-1 px-3 py-2 text-xs bg-zinc-800 border border-zinc-600 rounded text-gray-200 font-mono cursor-text focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
            <button
              id="copy-user-id-button"
              onClick={copyUserIdToClipboard}
              className="px-3 py-2 text-xs bg-emerald-600 text-white border-none rounded cursor-pointer whitespace-nowrap transition-colors duration-200 hover:bg-emerald-700 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            >
              Copy
            </button>
          </div>
          {impersonatedUserEmail && (
            <p className="mt-2 text-xs text-emerald-300">
              Acting as: {impersonatedUserEmail}
            </p>
          )}
        </div>
      )}

      {status?.has_secret_key ? (
        <div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '12px',
            padding: '8px 12px',
            background: '#1a2b1a',
            border: '1px solid #2d4a2d',
            borderRadius: '6px'
          }}>
            <span style={{ color: '#22c55e', fontSize: '12px' }}>●</span>
            <span style={{ color: '#e0e0e0', fontSize: '13px' }}>Secret key active</span>
          </div>
          
          <button
            onClick={revokeKey}
            disabled={loading}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: '12px',
              fontWeight: '500',
              backgroundColor: '#7c2d12',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = '#dc2626'
              }
            }}
            onMouseOut={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = '#7c2d12'
              }
            }}
          >
            {loading ? 'Revoking...' : 'Revoke Key'}
          </button>
        </div>
      ) : (
        <div>
          <div style={{
            marginBottom: '12px',
            padding: '8px 12px',
            background: '#2d2d1b',
            border: '1px solid #4a4a26',
            borderRadius: '6px'
          }}>
            <div style={{ color: '#eab308', fontSize: '12px', marginBottom: '4px' }}>
              ⚠️ No API key
            </div>
            <div style={{ color: '#b0b0b0', fontSize: '11px' }}>
              Generate a key to access the API
            </div>
          </div>

          <button
            onClick={generateKey}
            disabled={loading}
            style={{
              width: '100%',
              padding: '10px 16px',
              fontSize: '14px',
              fontWeight: '600',
              backgroundColor: '#1e40af',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
            onMouseOver={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = '#3b82f6'
                e.currentTarget.style.transform = 'translateY(-1px)'
              }
            }}
            onMouseOut={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = '#1e40af'
                e.currentTarget.style.transform = 'translateY(0)'
              }
            }}
          >
            <span>🔑</span>
            {loading ? 'Generating...' : 'Generate API Key'}
          </button>
        </div>
      )}

      {/* Generated Key Display */}
      {showKey && generatedKey && (
        <div style={{
          marginTop: '16px',
          padding: '16px',
          background: '#1a2b1a',
          border: '1px solid #22c55e',
          borderRadius: '8px'
        }}>
          <div style={{
            color: '#22c55e',
            fontSize: '14px',
            fontWeight: '600',
            marginBottom: '8px'
          }}>
            🎉 API Key Generated!
          </div>
          
          <div style={{
            color: '#b0b0b0',
            fontSize: '12px',
            marginBottom: '12px'
          }}>
            Copy this key now - it won't be shown again:
          </div>

          <div style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center'
          }}>
            <input
              type="text"
              value={generatedKey}
              readOnly
              style={{
                flex: 1,
                padding: '8px 12px',
                fontSize: '12px',
                background: '#2d2d30',
                border: '1px solid #404040',
                borderRadius: '4px',
                color: '#e0e0e0',
                fontFamily: 'monospace'
              }}
            />
            <button
              id="copy-button"
              onClick={copyToClipboard}
              style={{
                padding: '8px 12px',
                fontSize: '12px',
                background: '#22c55e',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              Copy
            </button>
          </div>

          <div style={{
            color: '#b0b0b0',
            fontSize: '11px',
            marginTop: '8px'
          }}>
            Use this key in the X-Secret-Key header for API requests
          </div>
        </div>
      )}
    </div>
  )
}

export default SecretKeyManager 
